import { memo, ReactNode, useCallback, useMemo, useState } from "react";
import { v1 } from "uuid";
import { ConfirmationContainer } from "./ConfirmationContainer";
import { ConfirmationContext, ConfirmationContextProps, ConfirmationProps } from "./ConfirmationContext";

export type ConfirmationProviderProps = {
  children?: ReactNode | ReactNode[]
}

export const ConfirmationProvider = memo<ConfirmationProviderProps>(({ children }) => {
  const confirmationsStorage = useMemo(() => ({ confirmations: [] as ConfirmationProps[] }), []);
  const [ confirmationsOnUI, setConfirmationsOnUI ] = useState<ConfirmationProps[]>(confirmationsStorage.confirmations);

  const push = useCallback((props: ConfirmationProps) => {
    const existingIndex = confirmationsStorage.confirmations.findIndex((item) => item.confirmationId === props.confirmationId);
    const index = existingIndex !== -1 ? existingIndex : confirmationsStorage.confirmations.length;

    const newConfirmationProps: ConfirmationProps = {
      ...confirmationsStorage.confirmations[existingIndex],
      ...props,
      confirmationId: props.confirmationId || v1(),
    };
    const newConfirmations = [
      ...confirmationsStorage.confirmations.slice(0, index),
      newConfirmationProps,
      ...confirmationsStorage.confirmations.slice(index + 1),
    ];

    Object.freeze(newConfirmationProps);
    Object.freeze(newConfirmations);

    confirmationsStorage.confirmations = newConfirmations;
    setConfirmationsOnUI(newConfirmations);

    return newConfirmationProps;
  }, [ confirmationsStorage, setConfirmationsOnUI ]);

  const remove = useCallback(
    (confirmationId: string | undefined) => {
      const newConfirmations = confirmationsStorage.confirmations.filter((props) => props.confirmationId !== confirmationId);
      Object.freeze(newConfirmations);
      confirmationsStorage.confirmations = newConfirmations;
      setConfirmationsOnUI(newConfirmations);
    },
    [ confirmationsStorage, setConfirmationsOnUI ],
  );

  const providerValue = useMemo(
    (): ConfirmationContextProps => ({
      confirmations: confirmationsOnUI,
      push,
      remove,
    }),
    [ confirmationsOnUI, push, remove ],
  );

  return (
    <ConfirmationContext.Provider value={providerValue}>
      {children}
      <ConfirmationContainer />
    </ConfirmationContext.Provider>
  );
});
